import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { Divider, MenuItem } from '@mui/material';

import { DrawerActionDropdown, PageDrawer } from 'common/components/PageDrawer';
import { PageTitle } from 'common/components/PageTitle';
import { Loader } from 'common/components/Loader';
import { useFrontendPermissions } from 'user/hooks';
import { getUrl } from 'route/utils/getUrl';
import { getCurrentUrl } from 'common/utils/getUrl';
import { useHashMatch } from 'route/hooks/useHashMatch';
import { ApolloError } from 'common/components/ApolloError';
import { useOrganisationSettingsForm } from 'organisation/hooks/organisationSettings/useOrganisationSettingsForm';
import { RouteLink } from 'route/components/RouteLink';

import { OrganisationSettingsForm } from '../forms/OrganisationSettingsForm';

interface IProps {
    open?: boolean;
}

export const OrganisationSettingsDrawer = ({ open }: IProps) => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const { canUpdate } = useFrontendPermissions('organisation');
    const organisationSettingsDrawerOpen = useHashMatch(
        'ORGANISATION_SETTINGS'
    );
    const { initialValues, loading, errors, handleSubmit } =
        useOrganisationSettingsForm();

    if (!organisationSettingsDrawerOpen) {
        return (
            <PageDrawer open={open}>
                <Loader />
            </PageDrawer>
        );
    }

    if (open && !canUpdate) {
        return <Navigate replace to={getUrl('PAGE_NOT_FOUND')} />;
    }

    // Redirect to the pathname to go to the page without hashtag.
    const handleClose = () => navigate(window.location.pathname);
    const nextUrl = getCurrentUrl({ fullUrl: true });

    const drawerActions = (
        <DrawerActionDropdown>
            <MenuItem
                component={RouteLink}
                query={`?next=${nextUrl}`}
                to="MANAGEMENT_SETTINGS_ADDRESS"
            >
                {translate('academySettingsForm.organisationAddress')}
            </MenuItem>
            <Divider />
            <MenuItem
                component={RouteLink}
                query={`?next=${nextUrl}`}
                to="MANAGEMENT_SETTINGS_DELETE"
            >
                {translate('academySettingsForm.deleteOrganisation')}
            </MenuItem>
        </DrawerActionDropdown>
    );

    return (
        <PageDrawer actions={drawerActions} open={open} onClose={handleClose}>
            {errors.map((error, index) => (
                <ApolloError error={error} key={index} />
            ))}

            {loading && <Loader />}

            {!loading && (
                <>
                    <PageTitle>
                        {translate('academySettingsForm.pageTitle')}
                    </PageTitle>

                    <OrganisationSettingsForm
                        initialValues={initialValues}
                        onClose={handleClose}
                        onSubmit={handleSubmit}
                    />
                </>
            )}
        </PageDrawer>
    );
};
